import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { ModalForm } from 'components/ModalForm';
import { useModal } from 'context/ModalProvider/useModal';
import { ValidationGroup } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';
import {
  useChangeContractorDeliveryPriceMutation,
  useGetContractorDeliveryPriceDetailQuery,
  useLazyGetContractorDeliveryPriceDetailQuery,
} from 'pages/Contractor/api/services';
import {
  ContractorDeliveryPriceCreate,
  ContractorDeliveryPriceUpdate,
} from 'pages/Contractor/ContractorDeliveryPrice.entity';
import $createDataSource from 'utils/create-data-souce-instance';
import { isSuccess } from 'utils/responseStatus';

import { ContractorDeliveryPriceForm } from '../../components';
import { CONTRACTOR_DELIVERY_PRICE_CRATE } from '../../ContractorDeliveryPrice.constants';

type Props = {
  onRefresh: () => void;
  contractorId: number | string;
  id: number;
};

export const EditContractorDeliveryPriceModal: FC<Props> = ({ onRefresh, contractorId, id }) => {
  const { hideModal } = useModal();
  const modalValidateRef = useRef<ValidationGroup>(null);

  const [form, setForm] = useState<ContractorDeliveryPriceCreate>(CONTRACTOR_DELIVERY_PRICE_CRATE);

  const [getContractorDeliveryPriceDetail] = useLazyGetContractorDeliveryPriceDetailQuery();
  const [changeContractorDeliveryPrice] = useChangeContractorDeliveryPriceMutation();

  const factoryDataStore: DataSource = useMemo(() => {
    const defaultFilter = ['parentId', '=', null];
    return $createDataSource('/v1/factory', 'id', { pageSize: 20, paginate: true, defaultFilter });
  }, []);

  const getContractorDetail = useCallback(async () => {
    getContractorDeliveryPriceDetail({ id })
      .unwrap()
      .then((data) => {
        if (isSuccess(data)) {
          setForm(data);
        }
      });
  }, [getContractorDeliveryPriceDetail, id]);

  useEffect(() => {
    getContractorDetail();
  }, [getContractorDetail]);

  const handleChange = (
    name: keyof ContractorDeliveryPriceCreate,
    value: ContractorDeliveryPriceCreate[keyof ContractorDeliveryPriceCreate],
  ) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const objectDataStore: DataSource = useMemo(() => {
    const defaultFilter = [['parentId', '=', null], 'and', ['type', '=', 1]];
    return $createDataSource(`/v1/ContractorObject/${contractorId}`, 'id', { defaultFilter });
  }, [contractorId]);

  const handleSubmit = () => {
    const isValid = modalValidateRef.current?.instance.validate().isValid;

    if (!isValid || !form) return;

    const updateData: ContractorDeliveryPriceUpdate = {
      ...form,
      id,
      contractorId: Number(contractorId),
    };

    changeContractorDeliveryPrice(updateData).then((data) => {
      if (isSuccess(data)) {
        onRefresh();
        hideModal();
        toast.success('Цена доставки успешно изменена', {
          position: 'bottom-right',
        });
      }
    });
  };

  return (
    <ModalForm
      onHidden={hideModal}
      title="Редактирование цены доставки"
      visible
      isLoadingSubmit={false}
      width={500}
      height="auto"
      textConfirm="Сохранить"
      hideOnOutsideClick={false}
      onSubmit={handleSubmit}
      ref={modalValidateRef}
    >
      <ContractorDeliveryPriceForm
        form={form}
        onChange={handleChange}
        factories={factoryDataStore}
        storeObject={objectDataStore}
        type="EDIT"
      />
    </ModalForm>
  );
};
